import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import { Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { iconButtonClasses } from '@mui/material/IconButton';

import { paths } from 'src/routes/paths';

import { useBoolean } from 'src/hooks/use-boolean';

import { CONFIG } from 'src/config-global';
import { allLangs, useTranslate } from 'src/locales';

import { Logo } from 'src/components/logo';
import { useSettingsContext } from 'src/components/settings';
import { UserIcon, LibraryIcon, BuildingIcon, DashboardIcon, ChecklistIcon } from 'src/components/custom/icons';

import CompanySwitcher from 'src/sections/companies/company-switcher';

import { useAuthContext } from 'src/auth/hooks';

import { Main } from './main';
import { NavMobile } from './nav-mobile';
import { layoutClasses } from '../classes';
import { NavVertical } from './nav-vertical';
import { ICONS } from '../config-nav-dashboard';
import { NavHorizontal } from './nav-horizontal';
// import { _account } from '../config-nav-account';
import { MenuButton } from '../components/menu-button';
import { LayoutSection } from '../core/layout-section';
import { HeaderSection } from '../core/header-section';
import { StyledDivider, useNavColorVars } from './styles';
import { AccountDrawer } from '../components/account-drawer';
import { SettingsButton } from '../components/settings-button';
import { LanguagePopover } from '../components/language-popover';
import { NotificationsDrawer } from '../components/notifications-drawer';

// ----------------------------------------------------------------------

export function DashboardLayout({ sx, children, header, data }) {
  const { t } = useTranslate('common');

  const theme = useTheme();

  const mobileNavOpen = useBoolean();

  const settings = useSettingsContext();

  const { user } = useAuthContext();

  const isAdmin = ["Owner", "Admin"].includes(user?.Role);

  const navColorVars = useNavColorVars(theme, settings);

  const layoutQuery = 'lg';

  // const navData = data?.nav ?? dashboardNavData;

  const navData = [
    // Audit Pulse Pro
    {
      subheader: CONFIG.appName,
      items: [
        // { title: 'Dashboard', path: paths.dashboard.root, icon: ICONS.dashboard },
        { title: t('jobs'), path: paths.jobs.root, icon: ICONS.job },
        isAdmin && { title: t('staff'), path: paths.staff.root, icon: ICONS.user },
        isAdmin && { title: t('clients'), path: paths.clients.root, icon: ICONS.blog },
        { title: t('library'), path: paths.library.root, icon: <LibraryIcon /> },
        { title: t('tasks'), path: paths.tasks.root, icon: <ChecklistIcon /> }
      ].filter(Boolean),
    },
  ];

  const account = [
    {
      label: t('dashboard'),
      href: paths.jobs.root,
      icon: <DashboardIcon />
    },
    {
      label: t('companies'),
      href: paths.companies.root,
      icon: <BuildingIcon />
    },
    {
      label: t('accountSettings'),
      href: paths.account.root,
      icon: <UserIcon />
    },
  ];


  const isNavMini = settings.navLayout === 'mini';
  const isNavHorizontal = settings.navLayout === 'horizontal';
  const isNavVertical = isNavMini || settings.navLayout === 'vertical';

  return (
    <LayoutSection
      /** **************************************
       * Header
       *************************************** */
      headerSection={
        <HeaderSection
          layoutQuery={layoutQuery}
          disableElevation={isNavVertical}
          slotProps={{
            toolbar: {
              sx: {
                ...(isNavHorizontal && {
                  bgcolor: 'var(--layout-nav-bg)',
                  [`& .${iconButtonClasses.root}`]: {
                    color: 'var(--layout-nav-text-secondary-color)',
                  },
                  [theme.breakpoints.up(layoutQuery)]: {
                    height: 'var(--layout-nav-horizontal-height)',
                  },
                }),
              },
            },
            container: {
              maxWidth: false,
              sx: {
                ...(isNavVertical && { px: { [layoutQuery]: 5 } }),
              },
            },
          }}
          sx={header?.sx}
          slots={{
            topArea: (
              <Alert severity="info" sx={{ display: 'none', borderRadius: 0 }}>
                This is an info Alert.
              </Alert>
            ),
            bottomArea: isNavHorizontal ? (
              <NavHorizontal
                data={navData}
                layoutQuery={layoutQuery}
                cssVars={navColorVars.section}
              />
            ) : null,
            leftArea: (
              <>
                {/* -- Nav mobile -- */}
                <MenuButton
                  onClick={mobileNavOpen.onTrue}
                  sx={{
                    mr: 1,
                    ml: -1,
                    [theme.breakpoints.up(layoutQuery)]: { display: 'none' },
                  }}
                />
                <NavMobile
                  data={navData}
                  open={mobileNavOpen.value}
                  onClose={mobileNavOpen.onFalse}
                  cssVars={navColorVars.section}
                />
                {/* -- Logo -- */}
                {isNavHorizontal && (
                  <Logo
                    sx={{
                      display: 'none',
                      [theme.breakpoints.up(layoutQuery)]: {
                        display: 'inline-flex',
                      },
                    }}
                  />
                )}
                {/* -- Divider -- */}
                {isNavHorizontal && user?.IsShowCompanyLogo && (
                  <StyledDivider
                    sx={{
                      [theme.breakpoints.up(layoutQuery)]: { display: 'flex' },
                    }}
                  />
                )}
                {isNavHorizontal && user && user?.IsShowCompanyName && user?.CompanyName && (
                  <Typography
                    variant="h4"
                    color={theme.vars.palette.primary.main}
                    sx={{
                      whiteSpace: 'nowrap',       // Prevents wrapping
                      overflow: 'hidden',        // Hides overflowing text
                      textOverflow: 'ellipsis',  // Adds "..." for truncated text
                      maxWidth: '100%',          // Ensures it respects container width
                      pr: 2,
                    }}
                  >
                    {user?.CompanyName}
                  </Typography>
                )}
                {/* -- Workspace popover -- */}
                {/* <WorkspacesPopover
                  data={_workspaces}
                  sx={{ color: "var(--layout-nav-text-primary-color)" }}
                /> */}
              </>
            ),
            rightArea: (
              <Box display="flex" alignItems="center" gap={{ xs: 0, sm: 0.75 }}>
                {/* -- Searchbar -- */}
                {/* <Searchbar data={navData} /> */}
                {/* -- Library -- */}
                {/* <LibraryIconButton /> */}
                {/* -- Notifications popover -- */}
                <NotificationsDrawer />
                {/* -- Contacts popover -- */}
                {/* <ContactsPopover data={_contacts} /> */}
                {/* -- Language popover -- */}
                <LanguagePopover data={allLangs} />
                {/* -- Settings button -- */}
                <SettingsButton />
                {/* Company Switcher */}
                <CompanySwitcher />
                {/* -- Account drawer -- */}
                <AccountDrawer data={account} />
              </Box>
            ),
          }}
        />
      }
      /** **************************************
       * Sidebar
       *************************************** */
      sidebarSection={
        isNavHorizontal ? null : (
          <NavVertical
            data={navData}
            isNavMini={isNavMini}
            layoutQuery={layoutQuery}
            cssVars={navColorVars.section}
            onToggleNav={() =>
              settings.onUpdateField(
                'navLayout',
                settings.navLayout === 'vertical' ? 'mini' : 'vertical'
              )
            }
          />
        )
      }
      /** **************************************
       * Footer
       *************************************** */
      footerSection={null}
      /** **************************************
       * Style
       *************************************** */
      cssVars={{
        ...navColorVars.layout,
        '--layout-transition-easing': 'linear',
        '--layout-transition-duration': '120ms',
        '--layout-nav-mini-width': '88px',
        '--layout-nav-vertical-width': '300px',
        '--layout-nav-horizontal-height': '64px',
        '--layout-dashboard-content-pt': theme.spacing(1),
        '--layout-dashboard-content-pb': theme.spacing(8),
        '--layout-dashboard-content-px': theme.spacing(5),
      }}
      sx={{
        [`& .${layoutClasses.hasSidebar}`]: {
          [theme.breakpoints.up(layoutQuery)]: {
            transition: theme.transitions.create(['padding-left'], {
              easing: 'var(--layout-transition-easing)',
              duration: 'var(--layout-transition-duration)',
            }),
            pl: isNavMini ? 'var(--layout-nav-mini-width)' : 'var(--layout-nav-vertical-width)',
          },
        },
        ...sx,
      }}
    >
      <Main isNavHorizontal={isNavHorizontal}>{children}</Main>
    </LayoutSection>
  );
}
